<template>
    <BaseDrawer
        v-model:open="open"
        :title="isEdit ? t('journeys.goalEditSidebar.editTitle') : t('journeys.goalEditSidebar.newTitle')"
        title-voice="plain"
        modal="mobile"
        :swipe-to-close="false"
        :dismissible="!isSubmitting"
        drawer-content-class="bg-surface z-50"
        overlay-class="z-40"
        @update:open="(value: boolean) => !value && emit('close')">
        <div class="px-6 py-5 flex flex-col gap-5">
            <!-- Title -->
            <div class="flex flex-col gap-2">
                <label for="goal-title-input" class="w-full flex flex-col gap-2">
                    <span class="scaffold-body-small text-control-label">{{ t("journeys.goalEditSidebar.titleLabel") }}</span>
                    <BaseInput id="goal-title-input" v-model="form.title" :placeholder="t('journeys.goalEditSidebar.titlePlaceholder')" :disabled="isSubmitting" />
                </label>
            </div>

            <!-- Description -->
            <div class="flex flex-col gap-2">
                <label for="goal-description-textarea" class="w-full flex flex-col gap-2">
                    <span class="scaffold-body-small text-control-label">{{ t("journeys.goalEditSidebar.descriptionLabel") }}</span>
                    <BaseTextarea
                        id="goal-description-textarea"
                        v-model:input="form.description"
                        rows="7"
                        disable-autosize
                        :disabled="isSubmitting"
                        :placeholder="t('journeys.goalEditSidebar.descriptionPlaceholder')"
                        class="resize-none rounded-md border border-control-border bg-control-bg p-2.5 text-control-text placeholder:text-control-placeholder"
                        :class="tonalThemeEnabled ? 'focus-field' : 'focus-ring'" />
                </label>
            </div>

            <!-- Assignee -->
            <div v-if="requireConnection || fixedConnection" class="flex flex-col gap-2">
                <span class="scaffold-body-small text-control-label">{{ t("journeys.goalEditSidebar.assigneeLabel") }}</span>

                <!-- Fixed (read-only) -->
                <div v-if="fixedConnection" class="flex flex-col gap-1">
                    <p class="nadia-title-subsection text-ink">{{ fixedConnection.name }}</p>
                    <p v-if="fixedConnection.job_title" class="body-small text-ink-muted">{{ fixedConnection.job_title }}</p>
                </div>

                <!-- Selectable -->
                <template v-else>
                    <div v-if="form.connection" class="flex items-start justify-between gap-2">
                        <div class="flex flex-col gap-1">
                            <p class="nadia-title-subsection text-ink">{{ form.connection.name }}</p>
                            <p v-if="form.connection.job_title" class="body-small text-ink-muted">{{ form.connection.job_title }}</p>
                        </div>
                        <BaseButton theme="tertiary" size="sm" :disabled="isSubmitting" @click="form.connection = null"> {{ t("journeys.connectionSearch.change") }} </BaseButton>
                    </div>
                    <ConnectionSearch v-else :disabled="isSubmitting" @select="form.connection = $event" />
                </template>
            </div>
        </div>
        <template #footer>
            <!-- Priority toggle (new personal goals only) -->
            <div v-if="canPrioritize && !isEdit" class="px-6 pb-4">
                <GoalPriorityToggleButton
                    :is-prioritized="form.isPriority"
                    :disabled="isSubmitting"
                    :prioritized-text="t('journeys.goalPriorityToggle.make')"
                    @click="form.isPriority = !form.isPriority" />
            </div>

            <!-- Footer -->
            <div class="border-t border-border">
                <div class="flex items-center justify-end gap-3 px-6 py-4">
                    <BaseButton theme="neutral" size="sm" :disabled="isSubmitting" @click="handleCancel">{{ t("common.cancel") }}</BaseButton>
                    <BaseButton theme="primary" size="sm" :disabled="!isValid || isSubmitting" @click="handleSubmit">
                        <i v-if="isSubmitting" class="bi bi-arrow-clockwise animate-spin" aria-hidden="true"></i>
                        {{ isEdit ? t("journeys.goalEditSidebar.saveChanges") : t("journeys.goalsSection.addGoal") }}
                    </BaseButton>
                </div>
            </div>
        </template>
    </BaseDrawer>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDrawer from "~vue/components/BaseDrawer.vue";
import BaseInput from "~vue/components/form/BaseInput.vue";
import BaseTextarea from "~vue/components/form/BaseTextarea.vue";
import { type GoalConnection, type GoalCreateEditData, GoalType } from "~vue/types/goals";
import { tonalThemeEnabled } from "~vue/utils/theme";
import { computed, reactive, watch } from "vue";
import { useI18n } from "vue-i18n";

import ConnectionSearch from "./ConnectionSearch.vue";
import GoalPriorityToggleButton from "./GoalPriorityToggleButton.vue";

const props = defineProps<{
    initialData?: Partial<GoalCreateEditData>;
    isSubmitting?: boolean;
    requireConnection?: boolean;
    fixedConnection?: GoalConnection | null;
    canPrioritize?: boolean;
}>();

const emit = defineEmits<{
    submit: [data: GoalCreateEditData];
    close: [];
}>();

const { t } = useI18n();

const open = defineModel<boolean>("open", { default: false });

const isEdit = computed(() => !!props.initialData?.id);

const form = reactive({
    title: props.initialData?.title ?? "",
    description: props.initialData?.description ?? "",
    goal_type: (props.initialData?.goal_type ?? GoalType.Development) as GoalType,
    connection: (props.initialData?.connection ?? null) as GoalConnection | null,
    isPriority: false,
});

watch(
    open,
    (val) => {
        if (val) {
            resetForm();
        }
    },
    { flush: "post" },
);

watch(
    () => props.isSubmitting,
    (val, prev) => {
        if (prev === true && val === false) {
            open.value = false;
        }
    },
);

const isValid = computed(() => form.title.trim() !== "" && ((!props.requireConnection && !props.fixedConnection) || form.connection !== null));

function cleanDescription(value: string): string {
    return value.replace(/\s+/g, " ").trim();
}

function resetForm() {
    form.title = props.initialData?.title ?? "";
    form.description = cleanDescription(props.initialData?.description ?? "");
    form.goal_type = props.initialData?.goal_type ?? GoalType.Development;
    form.connection = props.fixedConnection ?? props.initialData?.connection ?? null;
    form.isPriority = props.initialData?.is_prioritized ?? false;
}

function handleCancel() {
    if (props.isSubmitting) {
        return;
    }
    open.value = false;
    emit("close");
}

function handleSubmit() {
    if (!isValid.value || props.isSubmitting) {
        return;
    }
    emit("submit", {
        id: props.initialData?.id,
        title: form.title.trim(),
        description: form.description.trim(),
        goal_type: form.goal_type,
        team_member_goal: props.initialData?.team_member_goal ?? false,
        connection_id: form.connection?.id ?? props.initialData?.connection_id ?? null,
        connection: form.connection ?? props.initialData?.connection ?? null,
        is_prioritized: props.canPrioritize && !isEdit.value ? form.isPriority : undefined,
    });
}
</script>
